import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { maintenanceService } from '../../services/maintenance/planMaestroService';
import { toast } from 'react-hot-toast';
import { Loader2, Plus, Edit, Trash2, Wrench, Save, X } from 'lucide-react';

interface Mecanico {
    id: number;
    taller_id: number;
    nombre: string;
    especialidad: string;
    telefono: string;
    activo: boolean;
}

interface GestionMecanicosProps {
    isOpen: boolean;
    onClose: () => void;
    tallerId: number;
    tallerNombre: string;
}

export default function GestionMecanicos({ isOpen, onClose, tallerId, tallerNombre }: GestionMecanicosProps) {
    const [mecanicos, setMecanicos] = useState<Mecanico[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingMecanico, setEditingMecanico] = useState<Partial<Mecanico> | null>(null);

    const fetchMecanicos = async () => {
        try {
            setLoading(true);
            const response = await maintenanceService.getMecanicos(tallerId);
            setMecanicos(response.data);
        } catch (error) {
            console.error('Error fetching mecanicos:', error);
            toast.error('Error al cargar mecánicos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen && tallerId) {
            fetchMecanicos();
        }
    }, [isOpen, tallerId]);

    const handleSave = async () => {
        if (!editingMecanico?.nombre?.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }

        try {
            if (editingMecanico.id) {
                await maintenanceService.updateMecanico(tallerId, editingMecanico.id, editingMecanico);
                toast.success('Mecánico actualizado');
            } else {
                await maintenanceService.createMecanico(tallerId, editingMecanico);
                toast.success('Mecánico registrado');
            }
            setEditingMecanico(null);
            fetchMecanicos();
        } catch (error: any) {
            console.error('Error saving mecanico:', error);
            const msg = error.response?.data?.message || 'Error al guardar mecánico';
            toast.error(msg);
        }
    };

    const handleDelete = async (id: number) => {
        if (window.confirm('¿Estás seguro de eliminar este mecánico?')) {
            try {
                await maintenanceService.deleteMecanico(tallerId, id);
                toast.success('Mecánico eliminado');
                fetchMecanicos();
            } catch (error) {
                toast.error('Error al eliminar');
            }
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Wrench className="h-5 w-5 text-blue-600" />
                        Personal del Taller: {tallerNombre}
                    </DialogTitle>
                    <DialogDescription>
                        Administra los mecánicos asignados a este taller
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Formulario de Alta/Edición */}
                    {editingMecanico !== null && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 grid gap-4 grid-cols-1 md:grid-cols-4 items-end">
                            <div className="grid gap-2">
                                <label className="text-xs font-medium">Nombre</label>
                                <Input
                                    value={editingMecanico.nombre || ''}
                                    onChange={e => setEditingMecanico({ ...editingMecanico, nombre: e.target.value })}
                                    placeholder="Nombre completo"
                                    className="bg-white"
                                />
                            </div>
                            <div className="grid gap-2">
                                <label className="text-xs font-medium">Especialidad</label>
                                <Input
                                    value={editingMecanico.especialidad || ''}
                                    onChange={e => setEditingMecanico({ ...editingMecanico, especialidad: e.target.value })}
                                    placeholder="Ej. Motores, Eléctrico..."
                                    className="bg-white"
                                />
                            </div>
                            <div className="grid gap-2">
                                <label className="text-xs font-medium">Teléfono</label>
                                <Input
                                    value={editingMecanico.telefono || ''}
                                    onChange={e => setEditingMecanico({ ...editingMecanico, telefono: e.target.value })}
                                    placeholder="Opcional"
                                    className="bg-white"
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={handleSave} className="flex-1">
                                    <Save className="h-4 w-4 mr-1" /> {editingMecanico.id ? 'Actualizar' : 'Guardar'}
                                </Button>
                                <Button variant="outline" onClick={() => setEditingMecanico(null)}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {!editingMecanico && (
                        <div className="flex justify-end">
                            <Button onClick={() => setEditingMecanico({ nombre: '', especialidad: '', telefono: '', activo: true })} className="bg-blue-600 hover:bg-blue-700">
                                <Plus className="h-4 w-4 mr-2" /> Nuevo Mecánico
                            </Button>
                        </div>
                    )}

                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                        </div>
                    ) : mecanicos.length === 0 ? (
                        <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                            <p className="text-sm text-gray-500">No hay mecánicos registrados en este taller.</p>
                        </div>
                    ) : (
                        <div className="max-h-[300px] overflow-y-auto pr-2 space-y-2">
                            {mecanicos.map(mecanico => (
                                <div key={mecanico.id} className="flex items-center justify-between p-3 bg-white border border-gray-100 rounded-lg hover:shadow-sm transition-all shadow-sm">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <p className="font-semibold text-gray-800 text-sm">{mecanico.nombre}</p>
                                            <Badge variant={mecanico.activo ? 'default' : 'secondary'} className={`text-[10px] h-4 ${mecanico.activo ? 'bg-green-100 text-green-700' : ''}`}>
                                                {mecanico.activo ? 'Activo' : 'Inactivo'}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                            {mecanico.especialidad && <span>🛠️ {mecanico.especialidad}</span>}
                                            {mecanico.telefono && <span>📞 {mecanico.telefono}</span>}
                                        </div>
                                    </div>
                                    <div className="flex gap-1.5">
                                        <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5 text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => setEditingMecanico(mecanico)}>
                                            <Edit className="h-3.5 w-3.5" />
                                            <span className="text-xs font-medium">Editar</span>
                                        </Button>
                                        <Button variant="outline" size="sm" className="h-8 px-3 gap-1.5 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-600" onClick={() => handleDelete(mecanico.id)}>
                                            <Trash2 className="h-3.5 w-3.5" />
                                            <span className="text-xs font-medium">Eliminar</span>
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
